import { useMemo, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { ReferencesDict, SubsetFact } from '../../../../../../../../../../../../backend/src/types/types';
import { ButtonGroup } from '../../../../../../../../../components/shadcn/button-group';
import { DocChronologyDropdown } from '@/new/components/doc-chronology-dropdown';
import { FactDropdown } from '@/new/components/fact/fact-accordion';
import { FactChronologyDropdown } from '@/new/components/fact-chronology-dropdown';
import { Icon } from '@/new/components/icon';
import { Accordion } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { SubsetDropdown } from '@/new/components/subset-dropdown';
import { useStore } from '@/store';
import { ConfidenceLevelObject } from '@/types';

export interface SupportingEvidenceProps {
  confidenceLevel: ConfidenceLevelObject;
  facts: SubsetFact[];
  references: ReferencesDict;
  docIds: string[];
}

export function SupportingFacts({ confidenceLevel, facts, references, docIds }: SupportingEvidenceProps) {
  const { matterId, subsetId, threadId } = useParams();
  const navigate = useNavigate();
  const [selectedFactIds, setSelectedFactIds] = useState<Set<string>>(new Set());
  const { openReferenceDrawer } = useStore();

  const sortedFacts = useMemo(() => {
    const refByFactId = references
      ? Object.fromEntries(Object.entries(references).map(([key, value]) => [value.id, key]))
      : {};
    return [...facts].sort((a, b) => {
      const refA = parseInt(refByFactId[a.id] ?? '0');
      const refB = parseInt(refByFactId[b.id] ?? '0');
      return refA - refB;
    });
  }, [facts, references]);

  if (facts.length === 0 || !matterId || !threadId || !subsetId) {
    return null;
  }

  const handleFactSelectionChange = (factId: string, selected: boolean) => {
    setSelectedFactIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(factId);
      } else {
        next.delete(factId);
      }
      return next;
    });
  };

  const allRowsChecked = facts.length > 0 && selectedFactIds.size === facts.length;

  const toggleAllHandler = () => {
    if (allRowsChecked) {
      setSelectedFactIds(new Set());
    } else {
      setSelectedFactIds(new Set(facts.map((f) => f.id)));
    }
  };

  const deselectAllFacts = () => {
    setSelectedFactIds(new Set());
  };

  const referencesByFactId = references
    ? Object.fromEntries(Object.entries(references).map(([key, value]) => [value.id, key]))
    : null;

  return (
    <div className="mt-6">
      <div className="mx-2 mb-4 flex items-center justify-between">
        <div className="ml-1.75 flex items-center gap-2.5 text-sm">
          <Icon className="lucide--book text-base text-teal-500" />

          <h2 className="font-semibold text-gray-700">
            {confidenceLevel.text === 'Low' ? 'Possible Information' : 'Supporting Facts'}
            <span className="font-medium text-gray-500"> ({facts.length})</span>
          </h2>
        </div>

        <div className="flex gap-2">
          <SubsetDropdown
            matterId={matterId}
            subsetId={subsetId}
            docIds={docIds}
            size="sm"
            triggerText="Subset from Sources"
            onCreateNew={() => navigate(`/matters/${matterId}/subsets/new?documentIds=${docIds.join(',')}`)}
          />

          <ButtonGroup>
            <FactChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedFactIds={selectedFactIds}
              triggerText="Chronology from Selected Facts"
              size="sm"
            />
            <DocChronologyDropdown
              matterId={matterId}
              subsetId={subsetId}
              selectedDocIds={new Set(docIds)}
              size="sm"
              triggerText="Document Chronology from Sources"
              createDescription="Create a new document chronology from the source documents referenced in this answer"
              addDescription="Add the source documents referenced in this answer to an existing document chronology"
            />
          </ButtonGroup>

          <Button size="sm" asChild>
            <Link to={`/matters/${matterId}/subsets/${subsetId}/facts?threadId=${threadId}`}>
              <Icon className="lucide--search" />
              View in Facts
            </Link>
          </Button>
        </div>
      </div>

      <div className="mb-1 flex items-center gap-3 pl-4 text-xs text-gray-500">
        <Checkbox checked={allRowsChecked} onCheckedChange={toggleAllHandler} />
        <p>
          <strong>{selectedFactIds.size.toLocaleString()}</strong> Facts selected
        </p>

        <Button size="sm" disabled={selectedFactIds.size === 0} onClick={deselectAllFacts}>
          Clear All ({selectedFactIds.size}) Selected Facts
        </Button>
      </div>

      <Accordion type="multiple" className="flex flex-col overflow-auto">
        {sortedFacts.map((fact) => {
          return (
            <FactDropdown
              key={fact.id}
              fact={fact}
              subsetId={subsetId}
              reference={parseInt(referencesByFactId?.[fact.id] ?? '0')}
              editable={false}
              onViewDocument={() => openReferenceDrawer({ factId: fact.id, caseId: subsetId })}
              selectable={{
                isSelected: selectedFactIds.has(fact.id),
                handleSelect: (v) => handleFactSelectionChange(fact.id, !!v),
              }}
            />
          );
        })}
      </Accordion>
    </div>
  );
}
